import { GA4 } from 'src/externals/tag-manager/models/ga4.model';
import { AddLineItemModel, AddonModel, BasketResponseModel, BasketReturnModel, LineItemReturnModel, OrderModel, OrderReturnModel, Price, ProductAddonCard, ProductCardModel, ProductModel, UpdateBasketAddressModel, UpdateShippingModel } from '../services/client.generated';



export namespace MapToGA4 {

    interface Item extends GA4.Item {
        image_url?: string;
    }

    export function search(msg: string): GA4.Search {
        return {
            __event_name: 'search',
            search_term: msg
        };
    }
    function mapGoogleProductCategories(categories: string) {
        let _categories = {};
        if (categories != null && categories.length > 0)
            categories?.split(',')?.forEach((c, i) => _categories['item_category' + (i > 0 ? i + 1 : '')] = c)
        return _categories;
    }
    export function viewCart(response: BasketReturnModel): GA4.ViewCart {
        return {
            __event_name: 'view_cart',
            currency: response?.currency,
            value: response?.total?.amount,
            items: response?.lines?.map(lineItemReturnModel).reduce((accumilator, current) => accumilator.concat(current), []),
            basketId: response.basketId
        }
    }
    const VARIANT_SEPERATOR = ', ';
    export function itemVariant(item: LineItemReturnModel | ProductCardModel | ProductAddonCard | ProductModel | AddonModel): string | null {
        if ('selectedOptions' in item)
            return item.selectedOptions.join(VARIANT_SEPERATOR);
        if ('variantOptions' in item)
            return (item.variantOptions ?? []).map(vo => vo.value).join(VARIANT_SEPERATOR);
        if ('options' in item) {
            const vIds = (item.id.split(',').find((_, i) => i > 0) ?? '').split(';').filter(_ => _.length > 0);
            return vIds?.map(id => item.options[id].name)?.join(VARIANT_SEPERATOR);
        }
        return null;
    }
    export function checkoutStart(response: BasketResponseModel): GA4.BeginCheckout {
        return {
            __event_name: 'begin_checkout',
            currency: response?.basket?.total?.currency,
            value: response?.basket?.total?.amount,
            items: response?.basket?.lines?.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            basketId: response.basket.basketId
        };
    }
    export function mapProductToView(product: ProductModel): GA4.ViewItem {
        return {
            __event_name: 'view_item',
            value: product?.price?.salesPrice?.amount ?? null,
            currency: product?.price?.salesPrice?.currency ?? null,
            items: productModel(product)
        }
    }
    export function lineItemReturnModel(item: LineItemReturnModel): Array<Omit<Item, '__event_name'>> {
        let price = item.unitPrice?.listPrice?.amount;
        let discount = price - item.unitPrice?.salesPrice?.amount;
        return [{
            item_id: item.sku,
            item_name: item.name,
            item_variant: itemVariant(item),
            image_url: item.imageUrl,
            price,
            discount,
            item_brand: item.brand,
            ...mapGoogleProductCategories(item?.googleProductCategory),
            quantity: item.quantity,
        }]
    }
    export function productCardModel(item: ProductCardModel): Array<Omit<Item, '__event_name'>> {
        let quantity = 1;
        let price = item.price?.listPrice?.amount;
        let discount = price - item.price?.salesPrice?.amount;
        return [{
            item_id: item.id,
            item_name: item.name,
            image_url: item.images.find(() => true),
            quantity,
            item_variant: itemVariant(item),
            price,
            discount,
            item_brand: item.brand,
            ...mapGoogleProductCategories(item?.googleProductCategory),

        }, ...(item?.addons ?? []).map(addon => ({
            item_id: addon.id,
            item_name: addon.name,
            price: item?.price?.salesPrice?.amount,
            image_url: item.images.find(() => true),
            quantity,
            item_variant: itemVariant(addon),
            item_brand: addon.brand,
            ...mapGoogleProductCategories(addon?.googleProductCategory),
        }))]
    }
    export function productModel(item: ProductModel): Array<Omit<Item, '__event_name'>> {
        let price = priceSum(item, 'listPrice');
        let discount = price - priceSum(item, 'salesPrice');
        let quantity = 1;
        return [{
            item_id: item.id,
            item_name: item.name,
            item_variant: itemVariant(item),
            price,
            image_url: item.images.find(() => true),
            quantity,
            discount,
            item_brand: item.brand,
            ...mapGoogleProductCategories(item?.googleProductCategory),
        } as Omit<Item, '__event_name'>, ...(item?.addons ?? []).map(addon => ({
            item_id: addon.id,
            item_name: addon['name'] ?? '',
            item_variant: itemVariant(addon),
            image_url: item.images.find(() => true),
            item_brand: addon.brand,
            quantity,
            ...mapGoogleProductCategories(addon?.googleProductCategory),
        } as Omit<Item, '__event_name'>))]
    }

    // export function updateOrCreateLines(response: BasketResponseModel, itemsModel: AddLineItemModel, pastBasket: BasketReturnModel): GA4.AddToCart | GA4.RemoveFromCart {
    //     let lineChanges = (response?.basket?.lines
    //         ?.filter(line => [itemsModel.groupLine, ...(itemsModel?.lines ?? [])].some(_line => _line.sku === line.sku)) || []);
    //     let replacesLine = pastBasket.lines.find(l => l.id === itemsModel.replaces);
    //     let quantity = itemsModel.groupLine?.quantity ?? 1;
    //     if (replacesLine)
    //         quantity = quantity - replacesLine?.quantity;
    //     let items = lineChanges
    //         ?.map(lineItemReturnModel)
    //         ?.map(lines => lines.map(l => { l.quantity = Math.abs(quantity); return l; }))
    //         ?.reduce((acc, curr) => acc.concat(curr), []);
    //     let currency = response?.basket?.currency;
    //     let value = lineChanges.reduce((accumilator, current) => accumilator + calcAccPrice(current?.unitPrice?.salesPrice?.amount, quantity), 0);

    //     if (replacesLine && quantity < 0) {
    //         return { __event_name: 'remove_from_cart', items, currency, value }
    //     }
    //     return { __event_name: 'add_to_cart', items, currency, value, basketId: response.basket.basketId };
    // }

    function calcAccPrice(amount: number, quantity: number) {
        if (isNaN(amount) || isNaN(quantity) || amount == 0 || quantity == 0)
            return 0;
        return (amount * Math.abs(quantity))
    }

    export function updateOrCreateLine(response: BasketResponseModel, itemsModel: AddLineItemModel, pastBasket: BasketReturnModel): GA4.AddToCart | GA4.RemoveFromCart {
        let lineChanges = (response?.basket?.lines
            ?.filter(line => itemsModel.sku === line.sku));
        let currency = response?.basket?.currency;
        let quantityDelta = itemsModel.quantity - pastBasket.lines.find(line => itemsModel.sku === line.sku).quantity;
        let value = lineChanges.reduce((accumilator, current) => accumilator + calcAccPrice(current?.unitPrice?.salesPrice?.amount, quantityDelta), 0);
        let items = lineChanges
            ?.map(lineItemReturnModel)
            ?.map(lines => lines.map(l => { l.quantity = Math.abs(quantityDelta); return l; }))
            ?.reduce((acc, curr) => acc.concat(curr), []);
        if (quantityDelta < 0) {
            return { __event_name: 'remove_from_cart', items, currency, value }
        }
        return { __event_name: 'add_to_cart', items, currency, value, basketId: response.basket.basketId };
    }

    function priceSum(product: ProductModel, type: string): number {
        return [product?.price[type]?.amount ?? 0, ...(product?.addons ?? []).map(a => a.price[type]?.amount ?? 0)].reduce((a, c) => a + c, 0);
    }
    export function deleteLine(response: BasketResponseModel, ids: string[], pastBasket: BasketReturnModel): GA4.RemoveFromCart {
        const editedLines = (pastBasket?.lines?.filter(line => ids.some(_id => _id === line.id)) || []);
        return {
            __event_name: 'remove_from_cart',
            items: editedLines
                ?.map(lineItemReturnModel)
                ?.reduce((acc, curr) => acc.concat(curr), []),
            currency: response?.basket?.currency,
            value: editedLines.map(l => l.totalPrice.salesPrice.amount).reduce((a, c) => a + c, 0),
            basketId: response.basket.basketId
        };
    }
    export function deleteBasket(prev: BasketReturnModel): GA4.RemoveFromCart {
        return {
            __event_name: 'remove_from_cart',
            items: prev?.lines
                ?.map(lineItemReturnModel)
                ?.reduce((acc, curr) => acc.concat(curr), []),
            currency: prev?.currency,
            value: prev?.total?.amount,
            basketId: prev.basketId
        };
    }
    export function updateAddresses(response: BasketResponseModel, addresses: UpdateBasketAddressModel, prev: BasketReturnModel): GA4.BeginCheckout {
        return {
            __event_name: 'begin_checkout',
            items: response.basket.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.basket.total.amount,
            currency: response.basket.currency,
            basketId: response.basket.basketId
        }
    }
    export function updateNote(response: BasketResponseModel, note: string, prev: BasketReturnModel): GA4.BeginCheckout {
        return {
            __event_name: 'begin_checkout',
            items: response.basket.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.basket.total.amount,
            currency: response.basket.currency,
            basketId: response.basket.basketId
        }
    }
    export function setShipment(response: BasketResponseModel, shipping: UpdateShippingModel, prev: BasketReturnModel): GA4.AddShippingInfo {
        return {
            __event_name: 'add_shipping_info',
            items: response.basket.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.basket.total.amount,
            currency: response.basket.currency,
            shipping_tier: shipping.shippingMethodId,
            basketId: response.basket.basketId
        }
    }
    export function setShippingExtras(response: BasketResponseModel, keys: string[], prev: BasketReturnModel): GA4.AddShippingInfo {
        return {
            __event_name: 'add_shipping_info',
            items: response.basket.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.basket.total.amount,
            currency: response.basket.currency,
            shipping_tier: response.basket?.shipments?.find(() => true)?.shippingMethodId,
            extras: keys,
            basketId: response.basket.basketId
        }
    }
    export function setPayment(response: BasketReturnModel, paymentId: string): GA4.AddPaymentInfo {
        return {
            __event_name: 'add_payment_info',
            items: response.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.total.amount,
            currency: response.currency,
            payment_type: paymentId,
            basketId: response.basketId
        }
    }
    export function purchase(response: OrderModel): GA4.Purcahse {
        if (response['IsTracked'] === true)
            return;
        return {
            __event_name: 'purchase',
            items: response.lines.map(lineItemReturnModel)?.reduce((acc, curr) => acc.concat(curr), []),
            value: response.total.amount,
            currency: response.currency,
            transaction_id: response.orderId,
            tax: response.totalVat.amount,
            shipping: response.shippingTotal.amount,
            // affiliation: response.
            // coupon: response.
        }
    }
}
